import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "../utils/axios-instance";
import toast from "react-hot-toast";
import { Avatar } from "@mui/material";
import { useDepartment } from "../stores/departmentStore";
import OverviewTab from "../components/vendors/OverviewTab";
import DeliverablesTab from "../components/vendors/DeliverablesTab";

export default function VendorRoomDetail({ darkMode }) {
  const { roomId } = useParams();
  const { departments } = useDepartment();
  const deptMap = React.useMemo(() => {
    const map = {};
    (departments || []).forEach((d) => {
      map[d.id] = {
        name: d.name ? d.name.replace("_", " ") : d.id,
        bg: d.metadata?.bg || "#6366f1",
      };
    });
    return map;
  }, [departments]);
  const [activeTab, setActiveTab] = useState("Overview");
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dynamic user data states
  const [orgUsers, setOrgUsers] = useState([]);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");

  // Form states for whitelisting client emails
  const [clientEmailInput, setClientEmailInput] = useState("");
  const [submittingClientEmail, setSubmittingClientEmail] = useState(false);

  const tabs = [
    "Overview",
    "Deliverables",
    // "Receivables",
    // "Approval",
    // "Payment",
    // "Audit",
  ];

  // Sync room data and organization directory lists
  const initWorkspace = async () => {
    try {
      // 1. Fetch room specifics
      const roomRes = await api.get(`/vendors/rooms/`);
      const currentRoom = roomRes.data?.deliveryRooms?.find(
        (r) => r.id === roomId
      );
      if (currentRoom) {
        setRoom(currentRoom);
      } else {
        toast.error("Requested delivery room could not be resolved");
      }

      const usersRes = await api.get(`/users/list_users`);
      setOrgUsers(usersRes.data || []);
    } catch (err) {
      toast.error("Failed to sync room specifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initWorkspace();
  }, [roomId]);

  // Handler to link an organization employee to the room roster
  const handleAddMember = async (userObj) => {
    try {
      await api.post("/vendors/rooms/add-member/", {
        roomId: roomId,
        memberId: userObj.id,
        name: `${userObj.first_name} ${userObj.last_name}`,
        avatar: userObj.profilePictureUrl,
        department: userObj.dept,
      });
      toast.success("Team collaborator added successfully");
      setIsMemberModalOpen(false);
      setMemberSearch("");
      initWorkspace();
    } catch (err) {
      toast.error("Failed to add collaborator");
    }
  };

  // Handler to whitelist an explicit client email contact target
  const handleAddClientEmail = async (e) => {
    e.preventDefault();
    const emailClean = clientEmailInput.trim();
    if (!emailClean) return;

    // Simple email regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailClean)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Duplicate prevention check
    if (room.clientEmails?.includes(emailClean)) {
      toast.error("This email is already whitelisted for this room");
      return;
    }

    setSubmittingClientEmail(true);
    try {
      await api.post("/vendors/rooms/roster/", {
        roomId: roomId,
        type: "client",
        value: emailClean,
      });
      toast.success("Client email whitelisted successfully");
      setClientEmailInput("");
      initWorkspace();
    } catch (err) {
      toast.error("Failed to whitelist client email");
    } finally {
      setSubmittingClientEmail(false);
    }
  };

  // Filter strategy: Show users who are employees AND aren't already added
  const filteredAvailableEmployees = orgUsers.filter((u) => {
    const isEmployee =
      u.role_priv !== "org_admin" && u.role_priv !== "super_admin";
    const notInRoomYet = !room?.permittedUsers?.some(
      (member) => member.id === u.id
    );
    const matchesSearch =
      u.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
      `${u.first_name} ${u.last_name}`
        .toLowerCase()
        .includes(memberSearch.toLowerCase());
    return isEmployee && notInRoomYet && matchesSearch;
  });

  const theme = {
    bg: darkMode ? "#111827" : "#f9fafb",
    cardBg: darkMode ? "#1f2937" : "#ffffff",
    border: darkMode ? "#374151" : "#e5e7eb",
    textMain: darkMode ? "#f9fafb" : "#111827",
    textSub: darkMode ? "#9ca3af" : "#6b7280",
    inputBg: darkMode ? "#111827" : "#ffffff",
  };

  if (loading) return null;
  if (!room) return null;

  return (
    <div
      style={{
        background: theme.bg,
        minHeight: "100vh",
        padding: "32px",
        fontFamily: "inherit",
      }}
    >
      {/* HEADER SECTION */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: theme.textMain,
              margin: "0 0 4px 0",
            }}
          >
            {room.clientName}
          </h1>
          <p style={{ color: theme.textSub, margin: 0 }}>{room.projectName}</p>
        </div>
        <span
          style={{
            background: "#e6f4ea",
            color: "#137333",
            padding: "4px 12px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "600",
          }}
        >
          {room.status || "Pending"}
        </span>
      </div>

      {/* TABS CONTAINER ROW */}
      <div
        style={{
          display: "flex",
          gap: "24px",
          borderBottom: `1px solid ${theme.border}`,
          marginBottom: "24px",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: "transparent",
              border: "none",
              padding: "12px 0",
              cursor: "pointer",
              color: activeTab === tab ? "#5E5ADB" : theme.textSub,
              borderBottom: activeTab === tab ? "2px solid #5E5ADB" : "none",
              fontWeight: activeTab === tab ? "600" : "500",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ACTIVE PANELS INTERFACE */}
      {activeTab === "Overview" && (
        <OverviewTab
          room={room}
          roomId={roomId}
          darkMode={darkMode}
          theme={theme}
        />
      )}

      {activeTab === "Deliverables" && (
        <DeliverablesTab roomId={roomId} theme={theme} room={room} />
      )}

      {/* INTERNAL TEAM DIRECTORY DROPDOWN MODAL */}
      {isMemberModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1100,
          }}
        >
          <div
            style={{
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
              borderRadius: "12px",
              padding: "24px",
              width: "400px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h3
              style={{
                margin: "0 0 4px 0",
                color: theme.textMain,
                fontSize: "16px",
              }}
            >
              Add Team Member
            </h3>
            <p
              style={{
                fontSize: "12px",
                color: theme.textSub,
                marginBottom: "16px",
              }}
            >
              Select an employee from your organization directory to grant entry
              access.
            </p>

            <input
              type="text"
              placeholder="Search name..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
                background: theme.bg,
                color: theme.textMain,
                fontSize: "14px",
                marginBottom: "16px",
              }}
            />

            <div
              style={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              {filteredAvailableEmployees.length === 0 ? (
                <p
                  style={{
                    fontSize: "13px",
                    color: theme.textSub,
                    textAlign: "center",
                    padding: "20px 0",
                    fontStyle: "italic",
                  }}
                >
                  No eligible employees found.
                </p>
              ) : (
                filteredAvailableEmployees.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleAddMember(u)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      width: "100%",
                      padding: "10px",
                      background: "transparent",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = darkMode
                        ? "#2d3748"
                        : "#f3f4f6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <Avatar
                      src={u.profilePictureUrl}
                      sx={{ width: 32, height: 32, borderRadius: "8px" }}
                    />
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.textMain,
                        }}
                      >
                        {u.first_name} {u.last_name}
                      </div>
                      <div style={{ fontSize: "11px", color: theme.textSub }}>
                        {u.email}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
                borderTop: `1px solid ${theme.border}`,
                paddingTop: "12px",
              }}
            >
              <button
                onClick={() => {
                  setIsMemberModalOpen(false);
                  setMemberSearch("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: theme.textSub,
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
